import React from 'react';
import {
    Controller,
    FieldValues,
    UseFormRegister,
    RegisterOptions,
    Control,
} from 'react-hook-form';
import { useAppSelector } from '../../../../store/hooks/StoreHooks';

export interface DynamicTextareaProps {
    name: string;
    label?: string;
    placeholder?: string;
    className?: string;
    register?: UseFormRegister<FieldValues>;
    rules?: RegisterOptions;
    control?: Control<FieldValues>;
    error?: string;
    rows?: number;
    render?: (field: any) => React.ReactNode;
    [key: string]: any;
    onChange?: (e: any) => void;
    value?: string;
}

const Textarea: React.FC<DynamicTextareaProps> = ({
    name,
    label,
    placeholder,
    className,
    register,
    rules,
    control,
    error,
    rows = 4,
    render,
    onChange,
    value,
    ...rest
}) => {
    const { customerEnabled, customerCreated } = useAppSelector((state) => state.auth);
    const { projectReadOnly } = useAppSelector((state) => state.projectHeader);
    const isDisabled = (customerEnabled && !customerCreated) || projectReadOnly;
    
    return (
        <div className={className ? className : "flex flex-col gap-[6px]"}>
            {label && <label htmlFor={name} className={`flex flex-row justify-start items-start ${isDisabled ? 'text-neutral-200' : 'text-neutral-25'}`}>{label}</label>}
            {control ? (
                <Controller
                    name={name}
                    control={control}
                    rules={rules}
                    render={({ field }) =>
                    (
                        <textarea
                            id={name}
                            placeholder={placeholder}
                            rows={rows}
                            value={field.value || value || ""}
                            onChange={(e) => {
                                field.onChange(e.target.value);
                                onChange && onChange(e?.target?.value);
                            }}
                            aria-invalid={!!error}
                            disabled={isDisabled}
                            className={`border-darkcolor-400 border-[1px] py-[12px] px-[14px] rounded-[8px] bg-darkcolor-700 p-[16px] resize-none focus:outline-none focus:ring-1 focus:ring-darkcolor-700 ${isDisabled ? 'bg-gray-100 text-neutral-200 cursor-not-allowed' : 'text-neutral-25 hover:border-primary-500 focus:ring-primary-500'} ${error ? 'border-red-500' : ''}`}
                            maxLength={typeof rules?.maxLength === 'object'
                                ? rules.maxLength.value
                                : rules?.maxLength}
                        />
                    )
                    }
                />
            ) : register ? (
                <textarea
                    id={name}
                    placeholder={placeholder}
                    rows={rows}
                    {...register(name, rules)}
                    aria-invalid={!!error}
                    value={value}
                    onChange={(e) => {
                        onChange && onChange(e?.target?.value);
                    }}
                    disabled={isDisabled}
                    className={`border-darkcolor-400 border-[1px] py-[12px] px-[14px] rounded-[8px] bg-darkcolor-700 p-[16px] resize-none focus:outline-none focus:ring-1 focus:ring-darkcolor-700 ${isDisabled ? 'bg-gray-100 text-neutral-200 cursor-not-allowed' : 'text-neutral-25 hover:border-primary-500'} ${error ? 'border-red-500' : ''}`}
                    {...rest}
                    maxLength={typeof rules?.maxLength === 'object'
                        ? rules.maxLength.value
                        : rules?.maxLength}
                />
            ) : (
                <textarea
                    id={name}
                    placeholder={placeholder}
                    rows={rows}
                    aria-invalid={!!error}
                    value={value}
                    onChange={(e) => onChange && onChange(e?.target?.value)}
                    disabled={isDisabled}
                    className={`border-darkcolor-400 border-[1px] py-[12px] px-[14px] rounded-[8px] bg-darkcolor-700 p-[16px] resize-none focus:outline-none focus:ring-1 focus:ring-darkcolor-700 ${isDisabled ? 'bg-gray-100 text-neutral-200 cursor-not-allowed' : 'text-neutral-25 hover:border-primary-500'} ${error ? 'border-red-500' : ''}`}
                    {...rest}
                    maxLength={typeof rules?.maxLength === 'object'
                        ? rules.maxLength.value
                        : rules?.maxLength}
                />
            )}
            {error && <span style={{ color: 'red' }}>{error}</span>}
        </div>
    );
};

export default Textarea;
